import { useEffect, useState } from 'react'
import { Check, Palette, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSession } from '@/lib/auth-client'
import { isAdmin } from '@/server/domain/auth/authorization'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

interface ColorSystem {
  id: string
  name: string
  company: string
  gradient: string
  description: string
  dark: {
    primary: string
    secondary: string
    accent: string
    foreground: string
  }
  light: {
    primary: string
    secondary: string
    accent: string
    foreground: string
  }
}

const COLOR_SYSTEMS: Array<ColorSystem> = [
  {
    id: 'cursor',
    name: 'Electric Blue',
    company: 'Cursor',
    gradient: 'linear-gradient(135deg, #0066FF 0%, #0052cc 100%)',
    description: 'Bold blue with maximum contrast',
    dark: {
      primary: '#0066FF',
      secondary: '#3385ff',
      accent: '#66a3ff',
      foreground: '#ffffff',
    },
    light: {
      primary: '#0052cc',
      secondary: '#0066FF',
      accent: '#3385ff',
      foreground: '#ffffff',
    },
  },
  {
    id: 'sky-blue',
    name: 'Sky Blue',
    company: 'Modern SaaS',
    gradient: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
    description: 'Bright sky blue with soft vibes',
    dark: {
      primary: '#0EA5E9',
      secondary: '#38BDF8',
      accent: '#7DD3FC',
      foreground: '#ffffff',
    },
    light: {
      primary: '#0284C7',
      secondary: '#0EA5E9',
      accent: '#38BDF8',
      foreground: '#ffffff',
    },
  },
  {
    id: 'cobalt',
    name: 'Cobalt',
    company: 'Enterprise',
    gradient: 'linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%)',
    description: 'Deep cobalt for professional look',
    dark: {
      primary: '#3B82F6',
      secondary: '#60A5FA',
      accent: '#93C5FD',
      foreground: '#ffffff',
    },
    light: {
      primary: '#1E40AF',
      secondary: '#1E3A8A',
      accent: '#3B82F6',
      foreground: '#ffffff',
    },
  },
  {
    id: 'azure',
    name: 'Azure',
    company: 'Cloud Native',
    gradient: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
    description: 'Cyan-blue for modern tech',
    dark: {
      primary: '#06B6D4',
      secondary: '#22D3EE',
      accent: '#67E8F9',
      foreground: '#ffffff',
    },
    light: {
      primary: '#0891B2',
      secondary: '#06B6D4',
      accent: '#22D3EE',
      foreground: '#ffffff',
    },
  },
  {
    id: 'navy',
    name: 'Navy',
    company: 'Classic',
    gradient: 'linear-gradient(135deg, #001F3F 0%, #003366 100%)',
    description: 'Deep navy for premium feel',
    dark: {
      primary: '#3B82F6',
      secondary: '#60A5FA',
      accent: '#93C5FD',
      foreground: '#ffffff',
    },
    light: {
      primary: '#001F3F',
      secondary: '#003366',
      accent: '#1E40AF',
      foreground: '#ffffff',
    },
  },
  {
    id: 'perplexity',
    name: 'Ocean Teal',
    company: 'Perplexity',
    gradient: 'linear-gradient(135deg, #20808D 0%, #1a6b77 100%)',
    description: 'Professional teal with excellent contrast',
    dark: {
      primary: '#20808D',
      secondary: '#2d9cad',
      accent: '#4db8c4',
      foreground: '#ffffff',
    },
    light: {
      primary: '#0d5a66',
      secondary: '#1a7a85',
      accent: '#20808D',
      foreground: '#ffffff',
    },
  },
  {
    id: 'linear',
    name: 'Violet Pulse',
    company: 'Linear',
    gradient: 'linear-gradient(135deg, #5E6AD2 0%, #4c5bc7 100%)',
    description: 'Modern violet with strong hierarchy',
    dark: {
      primary: '#5E6AD2',
      secondary: '#7b85db',
      accent: '#9da5e8',
      foreground: '#ffffff',
    },
    light: {
      primary: '#3d42a8',
      secondary: '#4c5bc7',
      accent: '#5E6AD2',
      foreground: '#ffffff',
    },
  },
  {
    id: 'vercel',
    name: 'Monochrome',
    company: 'Vercel',
    gradient: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
    description: 'Minimalist black & white',
    dark: {
      primary: '#ffffff',
      secondary: '#e5e5e5',
      accent: '#cccccc',
      foreground: '#000000',
    },
    light: {
      primary: '#000000',
      secondary: '#1a1a1a',
      accent: '#333333',
      foreground: '#ffffff',
    },
  },
  {
    id: 'stripe',
    name: 'Royal Indigo',
    company: 'Stripe',
    gradient: 'linear-gradient(135deg, #635BFF 0%, #5348e8 100%)',
    description: 'Rich indigo with premium feel',
    dark: {
      primary: '#635BFF',
      secondary: '#7a73ff',
      accent: '#9b94ff',
      foreground: '#ffffff',
    },
    light: {
      primary: '#4c42cc',
      secondary: '#5348e8',
      accent: '#635BFF',
      foreground: '#ffffff',
    },
  },
]

function injectColorSystem(system: ColorSystem) {
  const existingStyle = document.getElementById('color-system-override')
  if (existingStyle) {
    existingStyle.remove()
  }

  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  // Create new style with comprehensive color system override
  const style = document.createElement('style')
  style.id = 'color-system-override'
  style.textContent = `
      /* Light Mode Color System */
      :root {
        --color-primary: ${system.light.primary};
        --color-secondary: ${system.light.secondary};
        --color-accent: ${system.light.accent};
        --color-primary-foreground: ${system.light.foreground};
        --color-primary-shadow: ${hexToRgba(system.light.primary, 0.2)};
        --color-secondary-shadow: ${hexToRgba(system.light.secondary, 0.2)};
        --color-accent-shadow: ${hexToRgba(system.light.accent, 0.2)};
        --color-focus-ring: ${hexToRgba(system.light.primary, 0.2)};
      }

      /* Dark Mode Color System */
      .dark {
        --color-primary: ${system.dark.primary};
        --color-secondary: ${system.dark.secondary};
        --color-accent: ${system.dark.accent};
        --color-primary-foreground: ${system.dark.foreground};
        --color-primary-shadow: ${hexToRgba(system.dark.primary, 0.4)};
        --color-secondary-shadow: ${hexToRgba(system.dark.secondary, 0.4)};
        --color-accent-shadow: ${hexToRgba(system.dark.accent, 0.4)};
        --color-focus-ring: ${hexToRgba(system.dark.primary, 0.2)};
      }
    `

  document.head.appendChild(style)
}

export function ColorSystemPicker() {
  const { data: session } = useSession()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedSystem, setSelectedSystem] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('color-system') || 'cobalt'
    }
    return 'cobalt'
  })

  const userIsAdmin = isAdmin(session?.user as any)

  // Apply saved (or default cobalt) color system on mount — admin-only, since
  // this overrides the app's real design tokens with `!important` for every
  // visitor of the page, not just the admin previewing it.
  useEffect(() => {
    if (!userIsAdmin) return
    const system = COLOR_SYSTEMS.find((s) => s.id === selectedSystem)
    if (system) injectColorSystem(system)
  }, [userIsAdmin, selectedSystem])

  // Only show for admin users
  if (!userIsAdmin) {
    return null
  }

  const applyColorSystem = (system: ColorSystem) => {
    injectColorSystem(system)
    // Save to localStorage
    localStorage.setItem('color-system', system.id)
    setSelectedSystem(system.id)
    setIsOpen(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {/* Floating Trigger Button */}
      <DialogTrigger asChild>
        <button
          className={cn(
            'fixed bottom-6 right-6 z-50',
            'w-14 h-14 rounded-full',
            'bg-primary text-primary-foreground',
            'glow-box hover:shadow-[0_0_40px_var(--color-primary-shadow)]',
            'transition-all duration-300 ease-brisk',
            'flex items-center justify-center',
            'hover:-translate-y-0.5 active:scale-[0.97]',
            'border border-[var(--color-border-strong)]',
          )}
          aria-label="Open color system picker"
        >
          <Palette className="w-6 h-6" />
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-4xl bg-[var(--color-bg-secondary)] rounded-3xl">
        {/* Header */}
        <DialogHeader className="flex-row items-start justify-between gap-4">
          <div>
            <DialogTitle className="text-2xl font-bold text-[var(--color-text-primary)]">
              Choose Your Color System
            </DialogTitle>
            <DialogDescription className="text-[var(--color-text-tertiary)] mt-1">
              Select from 5 modern SaaS design systems
            </DialogDescription>
          </div>
          <DialogClose
            className="p-2 hover:bg-[var(--color-control-surface-hover)] rounded-full transition-colors duration-300 ease-brisk"
            aria-label="Close color system picker"
          >
            <X className="w-5 h-5 text-[var(--color-text-secondary)]" />
          </DialogClose>
        </DialogHeader>

        {/* Color System Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {COLOR_SYSTEMS.map((system) => (
            <button
              key={system.id}
              onClick={() => applyColorSystem(system)}
              className={cn(
                'relative p-5 rounded-2xl',
                'border transition-all duration-300 ease-brisk',
                'hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)]',
                'text-left group',
                selectedSystem === system.id
                  ? 'border-primary/40 bg-primary/10'
                  : 'border-[var(--color-border)] hover:border-[var(--color-border-strong)]',
              )}
            >
              {/* Selected Indicator */}
              {selectedSystem === system.id && (
                <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[var(--color-success)] flex items-center justify-center">
                  <Check className="w-4 h-4 text-[var(--color-control-foreground)]" />
                </div>
              )}

              {/* Color Preview - Dark Mode */}
              <div className="mb-3">
                <p className="text-xs text-[var(--color-text-tertiary)] mb-2">
                  Dark Mode
                </p>
                <div
                  className="w-full h-12 rounded-2xl shadow-md flex items-center justify-center text-[var(--color-control-foreground)] font-semibold text-sm"
                  style={{ backgroundColor: system.dark.primary }}
                >
                  {system.name}
                </div>
              </div>

              {/* Color Preview - Light Mode */}
              <div className="mb-3">
                <p className="text-xs text-[var(--color-text-tertiary)] mb-2">
                  Light Mode
                </p>
                <div
                  className="w-full h-12 rounded-2xl shadow-md flex items-center justify-center font-semibold text-sm"
                  style={{
                    backgroundColor: system.light.primary,
                    color:
                      system.light.primary === '#000000'
                        ? '#ffffff'
                        : '#000000',
                  }}
                >
                  {system.name}
                </div>
              </div>

              {/* System Info */}
              <div className="space-y-2 border-t border-[var(--color-border)] pt-3">
                <div>
                  <h3 className="font-bold text-[var(--color-text-primary)] text-lg">
                    {system.name}
                  </h3>
                  <p className="text-xs text-[var(--color-text-tertiary)]">
                    Inspired by {system.company}
                  </p>
                </div>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {system.description}
                </p>

                {/* Color Swatches - Dark Mode */}
                <div className="pt-2">
                  <p className="text-xs text-[var(--color-text-tertiary)] mb-1.5">
                    Dark palette
                  </p>
                  <div className="flex gap-2">
                    <div
                      className="w-8 h-8 rounded-full border border-[var(--color-border-strong)]"
                      style={{ backgroundColor: system.dark.primary }}
                      title="Primary"
                    />
                    <div
                      className="w-8 h-8 rounded-full border border-[var(--color-border-strong)]"
                      style={{ backgroundColor: system.dark.secondary }}
                      title="Secondary"
                    />
                    <div
                      className="w-8 h-8 rounded-full border border-[var(--color-border-strong)]"
                      style={{ backgroundColor: system.dark.accent }}
                      title="Accent"
                    />
                  </div>
                </div>

                {/* Color Swatches - Light Mode */}
                <div>
                  <p className="text-xs text-[var(--color-text-tertiary)] mb-1.5">
                    Light palette
                  </p>
                  <div className="flex gap-2">
                    <div
                      className="w-8 h-8 rounded-full border border-[var(--color-border-strong)]"
                      style={{ backgroundColor: system.light.primary }}
                      title="Primary"
                    />
                    <div
                      className="w-8 h-8 rounded-full border border-[var(--color-border-strong)]"
                      style={{ backgroundColor: system.light.secondary }}
                      title="Secondary"
                    />
                    <div
                      className="w-8 h-8 rounded-full border border-[var(--color-border-strong)]"
                      style={{ backgroundColor: system.light.accent }}
                      title="Accent"
                    />
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <DialogFooter className="justify-center pt-4 border-t border-[var(--color-border)]">
          <p className="text-xs text-[var(--color-text-tertiary)] text-center">
            Your selection is saved locally and will persist across sessions
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
